import { Alert } from '@components/ui';
import type { PortalBanner } from '../types/announcement.types';
import { varianteDe } from '../types/announcement.types';

/**
 * Render de UN banner según su layout. Separado de `BannerSlot` para que la
 * vista previa del panel pinte exactamente lo mismo que verá el cliente: si el
 * panel tuviera su propio render, la previa mentiría en cuanto divergieran.
 */

/**
 * El contenido es HTML porque así nació el modelo: el portal legado lo inyecta
 * con `.html()` y los banners existentes están escritos así.
 *
 * Se renderiza tal cual SOLO porque el servidor lo pasa por el saneador de
 * lista blanca en CADA lectura (portalBanner.controller), no porque se confíe
 * en lo guardado — el JSON se edita a mano y ese camino no valida nada. Si
 * alguna vez se sanea únicamente al escribir, esto vuelve a ser el XSS
 * almacenado que ya tuvo este módulo.
 */
function Html({ html, className }: { html: string; className?: string }) {
  return <div className={className} dangerouslySetInnerHTML={{ __html: html }} />;
}

/** ¿El destino sale del portal? Los externos abren en pestaña nueva. */
function esExterno(destino: string): boolean {
  return /^https?:\/\//i.test(destino);
}

function BotonCta({ cta }: { cta: { texto: string; destino: string } }) {
  const externo = esExterno(cta.destino);
  return (
    <a
      href={cta.destino}
      {...(externo ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      className="inline-flex min-h-[42px] w-fit items-center rounded-[10px] bg-primary-600 px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-primary-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
    >
      {cta.texto}
    </a>
  );
}

export function BannerContenido({ banner }: { banner: PortalBanner }) {
  if (banner.layout === 'html') {
    // Lienzo a sangre: sin icono ni caja de Alert, porque el HTML trae su
    // propio maquetado y la caja se le pelearía.
    return (
      <div className="overflow-hidden rounded-xl border border-secondary-200 bg-white dark:border-secondary-700 dark:bg-secondary-800">
        {/* `overflow-x-auto`: un HTML pegado a mano no es responsive (no hay
            `@media` posible), así que en móvil se desplaza en su propia caja en
            vez de romper el ancho de la página. */}
        <div className="overflow-x-auto">
          <Html html={banner.contentHtml} />
        </div>
      </div>
    );
  }

  if (banner.layout === 'destacado') {
    return (
      <section className="overflow-hidden rounded-xl border border-secondary-200 bg-white p-6 shadow-[0_18px_44px_-32px_rgba(20,36,58,0.35)] dark:border-secondary-700 dark:bg-secondary-800 sm:p-7">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-center">
          {banner.imagen && (
            <img
              src={banner.imagen}
              alt=""
              className="h-24 w-full rounded-lg object-cover sm:h-28 sm:w-44 sm:shrink-0"
              // Una URL rota no debe dejar el hueco del icono de imagen partida.
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          )}

          <div className="flex min-w-0 flex-1 flex-col gap-2">
            {banner.titulo && (
              <h3 className="text-lg font-bold leading-tight text-secondary-900 dark:text-white sm:text-xl">
                {banner.titulo}
              </h3>
            )}
            <Html
              html={banner.contentHtml}
              className="text-sm leading-relaxed text-secondary-600 dark:text-secondary-300 [&_a]:font-medium [&_a]:underline"
            />
          </div>

          {banner.cta && (
            <div className="sm:shrink-0">
              <BotonCta cta={banner.cta} />
            </div>
          )}
        </div>
      </section>
    );
  }

  return (
    <Alert variant={varianteDe(banner.type)}>
      <Html html={banner.contentHtml} className="[&_a]:font-medium [&_a]:underline" />
    </Alert>
  );
}
